import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Avatar,
  Divider,
  Fade,
  Slide,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { 
  Close, 
  CloudUpload, 
  ArrowBack, 
  Publish, 
  Image as ImageIcon,
  LocationOn,
  AttachMoney,
  Category,
  Description,
  Inventory,
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward,
  Payment,
  AccountBalance,
  Save,
  Warning,
  Info,
  Error
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { userApiLoader } from "../../../action/loaderActions";
import axiosInstance from "../../../utils/axios/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../config/apiconfig";
import loadingGif from '../../../assets/images/loading.gif';

// Professional Loading Overlay Component
const LoadingOverlay = ({ isLoading, isPaymentCompleted, isDraftSaving }) => {
  if (!isLoading) return null;

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bgcolor="rgba(0, 0, 0, 0.8)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={9999}
      sx={{
        backdropFilter: 'blur(4px)',
        fontFamily: '"Saira", sans-serif',
      }}
    >
      <Card 
        elevation={12}
        sx={{ 
          p: 6, 
          borderRadius: 2,
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          textAlign: 'center',
          maxWidth: 400
        }}
      >
        <img
          src={loadingGif}
          alt="Loading..."
          style={{ width: 48, height: 48 }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            mt: 3, 
            fontWeight: 600,
            color: '#000',
            fontFamily: '"Saira", sans-serif'
          }}
        >
          Processing your request...
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1, 
            color: '#666',
            fontFamily: '"Saira", sans-serif'
          }}
        >
          {isDraftSaving ? 
            "Moving your listing to draft..." : 
            isPaymentCompleted ? 
              "Updating your paid listing..." : 
              "Please wait while we save your ad"
          }
        </Typography>
      </Card>
    </Box>
  );
};

// Custom Confirmation Dialog Component
const ConfirmationDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm", 
  cancelText = "Cancel",
  type = "warning" // warning, info, error, success
}) => {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return <Error sx={{ fontSize: 48, color: '#f44336', mb: 2 }} />;
      case 'info':
        return <Info sx={{ fontSize: 48, color: '#2196f3', mb: 2 }} />;
      case 'success':
        return <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />;
      default:
        return <Warning sx={{ fontSize: 48, color: '#ff9800', mb: 2 }} />;
    }
  };

  const getColor = () => {
    switch (type) {
      case 'error':
        return '#f44336';
      case 'info':
        return '#2196f3';
      case 'success':
        return '#4caf50';
      default:
        return '#ff9800';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
        {getIcon()}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            mb: 2,
            fontFamily: '"Saira", sans-serif',
            color: '#000'
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666',
            fontFamily: '"Saira", sans-serif',
            lineHeight: 1.6,
            whiteSpace: 'pre-line'
          }}
        >
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 1,
            px: 4,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '14px',
            fontFamily: '"Saira", sans-serif',
            color: '#666',
            borderColor: '#e0e0e0',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#999',
            }
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            borderRadius: 1,
            px: 4,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '14px',
            fontFamily: '"Saira", sans-serif',
            backgroundColor: getColor(),
            '&:hover': {
              backgroundColor: getColor(),
              filter: 'brightness(0.9)',
            }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Professional Stepper Component
const ProfessionalStepper = ({ activeStep, steps }) => {
  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          
          return (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: completed || active ? '#000' : '#e0e0e0',
                      color: completed || active ? 'white' : '#666',
                      fontWeight: 600,
                      fontSize: '14px',
                      fontFamily: '"Saira", sans-serif'
                    }}
                  >
                    {completed ? <CheckCircle sx={{ fontSize: 18 }} /> : index + 1}
                  </Box>
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontFamily: '"Saira", sans-serif',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#000' : '#666',
                    fontSize: '14px',
                    mt: 1
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

const CreateAdPage = () => {
  const { adId } = useParams();
  const isUpdate = Boolean(adId);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [newAd, setNewAd] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    sub_category_id: "",
    condition: "",
    shipping_method: "",
    payment_method_type: "online", // Add this new field: "online" or "cash"
    venmo_username: "",
    paypal_username: "",
    pickup_location: "",
    thumbnail: null,
    image: [],
    quantity: 1,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  const { accessToken } = useSelector((state) => state.user);
  const { isApiLoader } = useSelector((state) => state.loader || { isApiLoader: false });
  const [isImageLoading, setIsImageLoading] = useState(false);

  const steps = ['Basic Information', 'Category & Details', 'Images & Media'];

  // Professional theme styles
  const professionalTheme = {
    fontFamily: '"Saira", sans-serif',
    primary: '#ce55b9',
    black: '#000000',
    white: '#ffffff',
    gray: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121'
    }
  };

  // Helper function to show confirmation dialog
  const showConfirmDialog = (config) => {
    setConfirmDialog({
      open: true,
      ...config
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Updated validation rules - updated for cash only option
  const validationRules = {
    name: {
      required: true,
      maxLength: 255,
      message: "Listing title is required and cannot exceed 255 characters"
    },
    description: {
      required: true,
      message: "Description is required"
    },
    price: {
      required: true,
      min: 1,
      message: "Price is required and must be a positive number"
    },
    category_id: {
      required: true,
      message: "Category selection is required"
    },
    sub_category_id: {
      required: true,
      message: "Subcategory selection is required"
    },
    condition: {
      required: true,
      validValues: [1, 2, 3, 4, 5],
      message: "Item condition selection is required"
    },
    shipping_method: {
      required: true,
      validValues: [1, 2],
      message: "Shipping method selection is required"
    },
    pickup_location: {
      conditionallyRequired: true,
      condition: (formData) => formData.shipping_method === "1" || formData.shipping_method === 1,
      maxLength: 255,
      message: "Pickup location is required when shipping method is pickup only"
    },
    payment_method_type: {
      required: true,
      validValues: ["online", "cash"],
      message: "Payment method type selection is required"
    },
    // Updated payment methods validation
    payment_methods: {
      custom: true,
      validator: (formData) => {
        if (formData.payment_method_type === "cash") {
          return true; // Cash only is always valid
        }
        // For online payments, require at least one payment method
        const hasVenmo = formData.venmo_username && formData.venmo_username.trim() !== "";
        const hasPayPal = formData.paypal_username && formData.paypal_username.trim() !== "";
        return hasVenmo || hasPayPal;
      },
      message: "For online payments, at least one payment method (Venmo or PayPal) is required"
    }
  };

  // Step validation function
  const isCurrentStepValid = () => {
    switch (activeStep) {
      case 0: // Basic Information
        return (
          newAd.name && newAd.name.trim() !== "" &&
          newAd.description && newAd.description.trim() !== "" &&
          newAd.price && newAd.price.trim() !== "" && !isNaN(parseFloat(newAd.price)) && parseFloat(newAd.price) > 0
        );
      
      case 1: // Category & Details
        const hasBasicCategoryDetails = (
          newAd.category_id && newAd.category_id !== "" &&
          newAd.sub_category_id && newAd.sub_category_id !== "" &&
          newAd.condition && newAd.condition !== "" &&
          newAd.shipping_method && newAd.shipping_method !== "" &&
          newAd.payment_method_type && newAd.payment_method_type !== ""
        );
        
        const hasPickupLocationIfNeeded = (
          !shouldShowPickupLocation() || 
          (newAd.pickup_location && newAd.pickup_location.trim() !== "")
        );
        
        const hasPaymentMethod = (
          newAd.payment_method_type === "cash" ||
          (newAd.venmo_username && newAd.venmo_username.trim() !== "") ||
          (newAd.paypal_username && newAd.paypal_username.trim() !== "")
        );
        
        return hasBasicCategoryDetails && hasPickupLocationIfNeeded && hasPaymentMethod;
      
      case 2: // Images & Media
        return newAd.thumbnail !== null;
      
      default:
        return false;
    }
  };

  // Validation functions
  const shouldShowPickupLocation = () => {
    return newAd.shipping_method === "1" || newAd.shipping_method === 1;
  };

  const validateField = (fieldName, value) => {
    const rules = validationRules[fieldName];
    if (!rules) return "";

    if (!touched[fieldName]) return "";

    // Handle custom validation for payment methods
    if (rules.custom && rules.validator) {
      const isValid = rules.validator(newAd);
      if (!isValid) {
        return rules.message;
      }
      return "";
    }

    if (rules.conditionallyRequired && rules.condition) {
      const isRequired = rules.condition(newAd);
      if (isRequired) {
        const isEmpty = !value || value === "" || value === null || value === undefined;
        if (isEmpty) {
          return rules.message;
        }
      } else {
        return "";
      }
    } else if (rules.required) {
      const isEmpty = !value || value === "" || value === null || value === undefined;
      if (isEmpty) {
        return rules.message;
      }
    }

    const isEmpty = !value || value === "" || value === null || value === undefined;
    if (!isEmpty) {
      if (rules.maxLength && String(value).length > rules.maxLength) {
        return `This field cannot exceed ${rules.maxLength} characters`;
      }

      if (rules.min !== undefined || rules.max !== undefined) {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return `Please enter a valid number`;
        }
        if (rules.min !== undefined && numValue < rules.min) {
          return `Value must be at least ${rules.min}`;
        }
      }
    }

    return "";
  };

const validateAllFields = () => {
  const newErrors = {};

  Object.keys(validationRules).forEach(fieldName => {
    try {
      if (fieldName === 'pickup_location' && !shouldShowPickupLocation()) {
        return;
      }

      const fieldValue = newAd[fieldName];
      const error = validateField(fieldName, fieldValue);
      if (error) {
        newErrors[fieldName] = error;
      }
    } catch (err) {
      console.error(`Validation error for field ${fieldName}:`, err);
    }
  });

  // Thumbnail validation
  if (!newAd.thumbnail) {
    newErrors.thumbnail = "Thumbnail image is required";
  }

  // Additional images are now completely optional - no validation needed

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleInputChange = (field, value) => {
    setNewAd((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Special handling for payment methods validation
    if (field === 'venmo_username' || field === 'paypal_username' || field === 'payment_method_type') {
      setTouched((prev) => ({ ...prev, payment_methods: true }));
      const error = validateField('payment_methods', value);
      setErrors((prev) => ({
        ...prev,
        payment_methods: error
      }));
    } else {
      const error = validateField(field, value);
      setErrors((prev) => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const handleShippingMethodChange = (value) => {
    handleInputChange("shipping_method", value);

    if (value !== "1" && value !== 1) {
      setNewAd((prev) => ({ ...prev, pickup_location: "" }));
      setErrors((prev) => ({ ...prev, pickup_location: "" }));
      setTouched((prev) => ({ ...prev, pickup_location: false }));
    }
  };

  const handleFieldBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    
    // Special handling for payment methods validation
    if (fieldName === 'venmo_username' || fieldName === 'paypal_username' || fieldName === 'payment_method_type') {
      setTouched((prev) => ({ ...prev, payment_methods: true }));
      const error = validateField('payment_methods', newAd[fieldName]);
      setErrors((prev) => ({
        ...prev,
        payment_methods: error
      }));
    } else {
      const error = validateField(fieldName, newAd[fieldName]);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error
      }));
    }
  };

  const hasError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] && errors[fieldName] !== "";
  };

  const getErrorMessage = (fieldName) => {
    return hasError(fieldName) ? errors[fieldName] : "";
  };

  const handleCategoryChange = async (value) => {
    setNewAd((prev) => ({ ...prev, category_id: value, sub_category_id: "" }));
    setSubCategories([]);

    setErrors((prev) => ({ ...prev, sub_category_id: "" }));
    setTouched((prev) => ({ ...prev, category_id: true }));

    const error = validateField("category_id", value);
    setErrors((prev) => ({ ...prev, category_id: error }));

    if (!value) return;

    try {
      dispatch(userApiLoader({ isApiLoader: true }));
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/subcategories`,
        { category_id: value },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const subcategoryData = response.data?.data || [];
      setSubCategories(subcategoryData);
    } catch (error) {
      console.error("Subcategory fetch error:", error.response || error);
      toast.error("Failed to load subcategories");
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  const markPaymentCompleted = async (adId) => {
    showConfirmDialog({
      title: "Mark Payment as Completed",
      message: "Are you sure you want to mark this payment as completed? This action cannot be undone.",
      type: "warning",
      confirmText: "Mark Completed",
      cancelText: "Cancel",
      onConfirm: async () => {
        closeConfirmDialog();
        
        try {
          dispatch(userApiLoader({ isApiLoader: true }));
          
          const requestBody = {
            id: parseInt(adId),
            is_paid: 1
          };

          const response = await axiosInstance.post(
            `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/paid_status`,
            requestBody,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.data && response.data.status === 200) {
            toast.success(response.data.message || "Payment status updated successfully");
            setIsPaymentCompleted(true);
          } else {
            toast.error("Failed to update payment status");
          }
        } catch (error) {
          console.error("Payment Status Update Error:", error.response || error);
          
          if (error.response?.data?.errors) {
            const errorMessages = error.response.data.errors;
            if (Array.isArray(errorMessages)) {
              errorMessages.forEach(errorMsg => toast.error(errorMsg));
            } else {
              toast.error("Validation error occurred");
            }
          } else {
            toast.error(error.response?.data?.message || "Error updating payment status");
          }
        } finally {
          dispatch(userApiLoader({ isApiLoader: false }));
        }
      }
    });
  };

  // Draft function - Always enabled, no validation required
  const isDraftSaveEnabled = () => {
    return true;
  };

  // Updated helper function to create a properly sized dummy image file
  const createDummyImageFile = (filename = 'draft-placeholder.png') => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      // Set minimum required dimensions (50x50 or larger)
      canvas.width = 200;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      
      // Create a more professional looking placeholder
      // Background
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, 200, 200);
      
      // Border
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, 198, 198);
      
      // Placeholder text
      ctx.fillStyle = '#9e9e9e';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('DRAFT', 100, 90);
      ctx.fillText('IMAGE', 100, 110);
      
      // Small icon/shape
      ctx.fillStyle = '#bdbdbd';
      ctx.fillRect(85, 125, 30, 20);
      ctx.fillRect(90, 120, 20, 10);
      
      canvas.toBlob((blob) => {
        const dummyFile = new File([blob], filename, { type: 'image/png' });
        resolve(dummyFile);
      }, 'image/png', 0.8); // Added quality parameter
    });
  };

  // Enhanced function to save ad as draft - Sends empty strings for empty fields
  const saveAsDraft = async () => {
    const message = isUpdate 
      ? "Are you sure you want to move this listing to draft? This will make it invisible to buyers until you publish it again."
      : "Save your progress as a draft? You can continue editing and publish it later.";

    showConfirmDialog({
      title: isUpdate ? "Move to Draft" : "Save as Draft",
      message: message,
      type: "info",
      confirmText: "Save Draft",
      cancelText: "Cancel",
      onConfirm: async () => {
        closeConfirmDialog();
        
        try {
          setIsDraftSaving(true);
          dispatch(userApiLoader({ isApiLoader: true }));

          if (isUpdate && adId) {
            // For existing ads - use move_to_draft API
            const requestBody = {
              id: parseInt(adId),
              is_draft: 1
            };

            const response = await axiosInstance.post(
              `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/move_to_draft`,
              requestBody,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (response.data && response.data.status === 200) {
              toast.success(response.data.message || "Listing moved to draft successfully");
              setTimeout(() => {
                navigate("/myaccount/mylisting");
              }, 1500);
            } else {
              toast.error("Failed to move listing to draft");
            }
          } else {
            // For new ads - create as draft with empty strings for empty fields
            const formData = new FormData();

            // Send actual values or empty strings - no dummy values
            formData.append("name", newAd.name?.trim() || "");
            formData.append("description", newAd.description?.trim() || "");
            
            // For price, send empty string if not provided or invalid
            const priceValue = newAd.price && !isNaN(parseFloat(newAd.price)) ? parseFloat(newAd.price) : "";
            if (priceValue !== "") {
              formData.append("price", priceValue);
            } else {
              formData.append("price", "");
            }
            
            formData.append("quantity", 1);
            
            // Send selected condition or empty string
            formData.append("condition", newAd.condition || "");
            formData.append("shipping_method", newAd.shipping_method || "");

            // Handle payment method based on selection
            if (newAd.payment_method_type === "cash") {
              formData.append("payment_method", 1); // Cash payment
            } else if (newAd.payment_method_type === "online") {
              formData.append("payment_method", 2); // Online payment
              
              // Send actual payment methods or empty strings
              if (newAd.venmo_username && newAd.venmo_username.trim() !== "") {
                const venmoUsername = newAd.venmo_username.replace(/^@/, '');
                const venmoLink = priceValue !== "" ? 
                  `https://venmo.com/${venmoUsername}?txn=pay&amount=${priceValue}` :
                  `https://venmo.com/${venmoUsername}`;
                formData.append("venmo_id", venmoLink);
              } else {
                formData.append("venmo_id", "");
              }

              if (newAd.paypal_username && newAd.paypal_username.trim() !== "") {
                const paypalUsername = newAd.paypal_username.replace(/^@/, '');
                const paypalLink = priceValue !== "" ? 
                  `paypal.me/${paypalUsername}/${priceValue}` :
                  `paypal.me/${paypalUsername}`;
                formData.append("paypal_id", paypalLink);
              } else {
                formData.append("paypal_id", "");
              }
            } else {
              // No payment method type selected
              formData.append("payment_method", "");
              formData.append("venmo_id", "");
              formData.append("paypal_id", "");
            }

            // Send selected category and subcategory or empty strings
            if (newAd.category_id) {
              formData.append("category_id", parseInt(newAd.category_id));
            } else {
              formData.append("category_id", "");
            }
            
            if (newAd.sub_category_id) {
              formData.append("sub_category_id", parseInt(newAd.sub_category_id));
            } else {
              formData.append("sub_category_id", "");
            }

            // Handle pickup location - send empty string if not applicable or not provided
            if ((newAd.shipping_method === "1" || newAd.shipping_method === 1)) {
              formData.append("pickup_location", newAd.pickup_location?.trim() || "");
            } else {
              formData.append("pickup_location", "");
            }

            // Mark as draft
            formData.append("is_draft", 1);

            // Handle thumbnail - only include if provided, otherwise send empty
            if (newAd.thumbnail) {
              formData.append("thumbnail", newAd.thumbnail);
            } else {
              // For drafts, we could either skip the thumbnail entirely or send empty
              // Since the API might require it, we'll create a minimal placeholder
              // But if you want to send empty string, you'd need to check API requirements
              const dummyThumbnail = await createDummyImageFile('draft-thumbnail.png');
              formData.append("thumbnail", dummyThumbnail);
            }

            // Handle additional images - only include if provided
            if (selectedImageFiles && selectedImageFiles.length > 0) {
              selectedImageFiles.forEach((file, index) => {
                formData.append(`image[${index}]`, file);
              });
            }
            // If no additional images, don't append anything (empty)

            // Make the API call
            const response = await axiosInstance.post(
              `${API.USER_MANAGEMENT_API_BASE_URL}/market-place/v1/ads/create`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            // If we get here, the API call was successful
            toast.success("Listing saved as draft successfully");
            setTimeout(() => {
              navigate("/myaccount/mylisting");
            }, 1500);
          }
        } catch (error) {
          console.error("Save as Draft Error:", error.response || error);
          
          // Handle different types of errors
          if (error.response?.status === 400 && error.response?.data?.errors) {
            const errors = error.response.data.errors;
            
            // Log the validation errors for debugging
            console.log("Validation errors for draft:", errors);
            
            // For drafts, we might expect validation errors for missing required fields
            // You can choose to show success anyway or handle specific cases
            if (Array.isArray(errors)) {
              // Check if these are acceptable draft validation errors
              const draftAcceptableErrors = [
                "name", "description", "price", "category_id", "sub_category_id", 
                "condition", "shipping_method", "thumbnail", "image"
              ];
              
              const hasOnlyDraftErrors = errors.every(error => 
                draftAcceptableErrors.some(acceptable => 
                  error.toLowerCase().includes(acceptable.toLowerCase())
                )
              );
              
              if (hasOnlyDraftErrors) {
                toast.success("Draft saved with current information. Complete the missing details when ready to publish.");
                setTimeout(() => {
                  navigate("/myaccount/mylisting");
                }, 1500);
              } else {
                // Show actual validation errors
                errors.forEach(errorMsg => toast.error(errorMsg));
              }
            } else {
              toast.error("Validation error occurred while saving draft");
            }
          } else if (error.response?.status === 401) {
            toast.error("Session expired. Please login again.");
          } else if (error.response?.status === 500) {
            toast.error("Server error. Please try again later.");
          } else {
            toast.error("Failed to save draft. Please try again.");
          }
        } finally {
          setIsDraftSaving(false);
          dispatch(userApiLoader({ isApiLoader: false }));
        }
      }
    });
  };

  // New handler for draft button that prevents validation
  const handleDraftClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any existing errors before saving draft
    setErrors({});
    setTouched({});
    
    await saveAsDraft();
  };

  const submitAd = async () => {
    const allFields = Object.keys(validationRules);
    const touchedFields = {};
    allFields.forEach(field => {
      if (field === 'pickup_location' && !shouldShowPickupLocation()) {
        return;
      }
      touchedFields[field] = true;
    });
    
    // Add payment methods to touched fields
    touchedFields.payment_methods = true;
    setTouched(touchedFields);

    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    // Show confirmation dialog before publishing
    const message = `Are you ready to publish this ad?\n\nOnce published, your listing will be visible to all buyers. You can also save it as a draft if you want to continue editing later.\n\nClick "Publish Now" to publish, or "Cancel" to save as draft instead.`;

    showConfirmDialog({
      title: "Publish Your Listing",
      message: message,
      type: "info",
      confirmText: "Publish Now",
      cancelText: "Save as Draft",
      onConfirm: async () => {
        closeConfirmDialog();
        await performSubmit();
      }
    });

    // Override the cancel button to save as draft
    const originalCancel = confirmDialog.onCancel;
    setConfirmDialog(prev => ({
      ...prev,
      onCancel: () => {
        closeConfirmDialog();
        handleDraftClick();
      }
    }));
  };

  const performSubmit = async () => {
    dispatch(userApiLoader({ isApiLoader: true }));

    const formData = new FormData();

    formData.append("category_id", parseInt(newAd.category_id));
    formData.append("sub_category_id", parseInt(newAd.sub_category_id));
    formData.append("name", newAd.name);
    formData.append("description", newAd.description);
    formData.append("price", parseFloat(newAd.price));
    formData.append("quantity", 1);
    formData.append("condition", parseInt(newAd.condition));
    formData.append("shipping_method", parseInt(newAd.shipping_method));

    // Handle payment method based on selection
    if (newAd.payment_method_type === "cash") {
      formData.append("payment_method", 1); // Cash payment
    } else {
      formData.append("payment_method", 2); // Online payment
      
      // Add payment methods only for online payments
      if (newAd.venmo_username && newAd.venmo_username.trim() !== "") {
        const venmoUsername = newAd.venmo_username.replace(/^@/, '');
        const venmoLink = `https://venmo.com/${venmoUsername}?txn=pay&amount=${parseFloat(newAd.price)}`;
        formData.append("venmo_id", venmoLink);
      }

      if (newAd.paypal_username && newAd.paypal_username.trim() !== "") {
        const paypalUsername = newAd.paypal_username.replace(/^@/, '');
        const paypalLink = `paypal.me/${paypalUsername}/${parseFloat(newAd.price)}`;
        formData.append("paypal_id", paypalLink);
      }
    }

    if (shouldShowPickupLocation() && newAd.pickup_location) {
      formData.append("pickup_location", newAd.pickup_location);
    }

    formData.append("is_draft", 0);

    if (isUpdate) {
      formData.append("id", adId);
    }

    if (newAd.thumbnail) {
      formData.append("thumbnail", newAd.thumbnail);
    }

    // Additional images are now optional - only include if they exist
    if (selectedImageFiles.length > 0) {
      selectedImageFiles.forEach((file, index) => {
        formData.append(`image[${index}]`, file);
      });
    }

    // Define API URL outside try block for use in catch block
    const apiUrl = isUpdate
      ? `${API.USER_MANAGEMENT_API_BASE_URL}/market-place/v1/ads/update`
      : `${API.USER_MANAGEMENT_API_BASE_URL}/market-place/v1/ads/create`;

    try {
      const response = await axiosInstance.post(apiUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const successMessage = isUpdate ? 
        (isPaymentCompleted ? "Listing updated successfully - Payment already completed" : "Listing updated successfully") : 
        "Listing created successfully";
      
      toast.success(response.data?.message || successMessage);
      navigate("/myaccount/mylisting");
    } catch (error) {
      console.error("Listing Submit Error:", error.response || error);
      
      // Check if the error is specifically about missing images
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errorMessages = error.response.data.errors;
        const imageErrors = Array.isArray(errorMessages) ? 
          errorMessages.filter(err => err.toLowerCase().includes('image')) : [];
        
        if (imageErrors.length > 0 && selectedImageFiles.length === 0) {
          // API requires at least one image - create a dummy one and retry
          try {
            const dummyImage = await createDummyImageFile('placeholder-image.png');
            formData.append("image[0]", dummyImage);
            
            const retryResponse = await axiosInstance.post(apiUrl, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${accessToken}`,
              },
            });
            
            const successMessage = isUpdate ? 
              (isPaymentCompleted ? "Listing updated successfully - Payment already completed" : "Listing updated successfully") : 
              "Listing created successfully";
            
            toast.success(retryResponse.data?.message || successMessage);
            navigate("/myaccount/mylisting");
            return;
          } catch (retryError) {
            console.error("Retry with dummy image failed:", retryError);
          }
        }
        
        // Handle other validation errors
        if (Array.isArray(errorMessages)) {
          errorMessages.forEach(errorMsg => toast.error(errorMsg));
        } else {
          toast.error("Validation error occurred");
        }
      } else {
        toast.error(error.response?.data?.message || "Error submitting ad");
      }
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        dispatch(userApiLoader({ isApiLoader: true }));
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/categories`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const categoriesData = response.data?.data?.categories || response.data?.data || response.data || [];
        setCategories(categoriesData);
      } catch (error) {
        console.error("Category fetch error:", error.response || error);
        toast.error("Failed to load categories");
      } finally {
        dispatch(userApiLoader({ isApiLoader: false }));
      }
    };

    if (accessToken) {
      fetchCategories();
    }
  }, [accessToken, dispatch]);

  // Fetch existing ad data when updating
  useEffect(() => {
    const fetchAdData = async () => {
      if (!isUpdate || !adId || !accessToken) return;

      try {
        dispatch(userApiLoader({ isApiLoader: true }));
        const response = await axiosInstance.get(
          `${API.USER_MANAGEMENT_API_BASE_URL}/market-place/v1/ads/${adId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const adData = response.data?.data;
        console.log("Listing Data from API:", adData);
        
        if (adData) {
          // Check payment status
          const isPaid = adData.is_paid === 1 || adData.is_paid === "1" || adData.is_paid === true;
          console.log("Setting payment completed to:", isPaid);
          setIsPaymentCompleted(isPaid);
          
          if (isPaid) {
            toast.success("Payment has been completed by the buyer");
          }

          // Extract payment usernames from URLs if they exist
          let venmoUsername = "";
          let paypalUsername = "";
          let paymentMethodType = "online"; // Default to online
          
          // Check if this is a cash-only ad
          if (adData.payment_method === 1 || adData.payment_method === "1") {
            paymentMethodType = "cash";
          } else {
            if (adData.venmo_payment_id) {
              // Extract username from venmo URL: https://venmo.com/username?txn=pay&amount=...
              const venmoMatch = adData.venmo_payment_id.match(/venmo\.com\/([^?]+)/);
              if (venmoMatch) {
                venmoUsername = venmoMatch[1];
              }
            }
            
            if (adData.paypal_payment_id) {
              // Extract username from paypal URL: paypal.me/username/amount
              const paypalMatch = adData.paypal_payment_id.match(/paypal\.me\/([^\/]+)/);
              if (paypalMatch) {
                paypalUsername = paypalMatch[1];
              }
            }
          }

          // Populate form with existing data
          setNewAd({
            name: adData.title || "",
            description: adData.description || "",
            price: adData.price || "",
            category_id: "", // Will be set after categories load
            sub_category_id: adData.sub_category_id || "",
            quantity: 1, // Always default to 1
            condition: adData.condition || "",
            shipping_method: adData.shipping_option || "",
            payment_method_type: paymentMethodType,
            venmo_username: venmoUsername,
            paypal_username: paypalUsername,
            pickup_location: adData.location || "",
            thumbnail: null,
            image: [],
          });

          // Set existing images if available
          if (adData.thumbnail && Array.isArray(adData.thumbnail) && adData.thumbnail.length > 0) {
            setThumbnailPreview(adData.thumbnail[0]);
          }
          
          if (adData.images && Array.isArray(adData.images)) {
            const imageUrls = adData.images.map(img => img.image_url);
            setImagePreviewUrls(imageUrls);
          }

          // Handle category matching after categories are loaded
          if (adData.category_name) {
            setTimeout(() => {
              if (categories.length > 0) {
                const matchingCategory = categories.find(cat => cat.name === adData.category_name);
                if (matchingCategory) {
                  setNewAd(prev => ({...prev, category_id: matchingCategory.id}));
                  
                  // Load subcategories
                  axiosInstance.post(
                    `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/subcategories`,
                    { category_id: matchingCategory.id },
                    {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                      },
                    }
                  ).then(subResponse => {
                    const subcategoryData = subResponse.data?.data || [];
                    setSubCategories(subcategoryData);
                  }).catch(err => {
                    console.error("Error loading subcategories:", err);
                  });
                }
              }
            }, 500);
          }
        }
      } catch (error) {
        console.error("Listing fetch error:", error.response || error);
        
        if (error.response?.data?.errors) {
          const errorMessages = error.response.data.errors;
          if (Array.isArray(errorMessages)) {
            errorMessages.forEach(errorMsg => toast.error(errorMsg));
          } else {
            toast.error("Error loading listing data");
          }
        } else if (error.response?.status === 404) {
          toast.error("Listing not found");
          navigate("/myaccount/mylisting");
        } else {
          toast.error(error.response?.data?.message || "Failed to load listing data");
        }
      } finally {
        dispatch(userApiLoader({ isApiLoader: false }));
      }
    };

    fetchAdData();
  }, [isUpdate, adId, accessToken, dispatch, navigate]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderBasicInformation = () => (
    <Card 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        backgroundColor: professionalTheme.white,
        border: `1px solid ${professionalTheme.gray[300]}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: professionalTheme.black,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
            }}
          >
            <Description sx={{ color: professionalTheme.white, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Basic Information
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily
              }}
            >
              Provide essential details about your item
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Listing Title"
              placeholder="Enter a descriptive title for your listing"
              value={newAd.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              onBlur={() => handleFieldBlur("name")}
              error={hasError("name")}
              helperText={getErrorMessage("name")}
              disabled={isApiLoader}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: professionalTheme.fontFamily,
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Price ($)"
              type="number"
              placeholder="0"
              value={newAd.price}
              onChange={(e) => handleInputChange("price", e.target.value)}
              onBlur={() => handleFieldBlur("price")}
              error={hasError("price")}
              helperText={getErrorMessage("price") || "Enter price (decimals allowed, e.g., 25.99)"}
              disabled={isApiLoader}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: professionalTheme.fontFamily,
                }
              }}
              InputProps={{
                startAdornment: <AttachMoney sx={{ mr: 1, color: professionalTheme.gray[600] }} />
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              placeholder="Provide detailed information about your item..."
              value={newAd.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              onBlur={() => handleFieldBlur("description")}
              error={hasError("description")}
              helperText={getErrorMessage("description") || "Be descriptive to attract more buyers"}
              disabled={isApiLoader}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: professionalTheme.fontFamily,
                }
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderCategoryAndDetails = () => (
    <Card 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        backgroundColor: professionalTheme.white,
        border: `1px solid ${professionalTheme.gray[300]}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: professionalTheme.black,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
            }}
          >
            <Category sx={{ color: professionalTheme.white, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Category & Details
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily
              }}
            >
              {isPaymentCompleted ? 
                "Item details and payment information (Payment completed ✅)" : 
                "Categorize your item and provide selling details"
              }
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("category_id")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                }
              }}
            >
              <InputLabel>Category</InputLabel>
              <Select
                value={newAd.category_id}
                onChange={(e) => handleCategoryChange(e.target.value)}
                onBlur={() => handleFieldBlur("category_id")}
                label="Category"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                {categories.map((category) => (
                  <MenuItem 
                    key={category.id} 
                    value={category.id}
                    sx={{ fontFamily: professionalTheme.fontFamily }}
                  >
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {hasError("category_id") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("category_id")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("sub_category_id")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                }
              }}
            >
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={newAd.sub_category_id}
                onChange={(e) => handleInputChange("sub_category_id", e.target.value)}
                onBlur={() => handleFieldBlur("sub_category_id")}
                label="Subcategory"
                disabled={!newAd.category_id || isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                {subCategories.map((subCategory) => (
                  <MenuItem 
                    key={subCategory.id} 
                    value={subCategory.id}
                    sx={{ fontFamily: professionalTheme.fontFamily }}
                  >
                    {subCategory.name}
                  </MenuItem>
                ))}
              </Select>
              {hasError("sub_category_id") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("sub_category_id")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("condition")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                }
              }}
            >
              <InputLabel>Item Condition</InputLabel>
              <Select
                value={newAd.condition}
                onChange={(e) => handleInputChange("condition", e.target.value)}
                onBlur={() => handleFieldBlur("condition")}
                label="Item Condition"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                <MenuItem value={1} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="New" 
                      color="success" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    New
                  </Box>
                </MenuItem>
                <MenuItem value={2} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Like New" 
                      color="info" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Like New
                  </Box>
                </MenuItem>
                <MenuItem value={3} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Good" 
                      color="primary" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Good
                  </Box>
                </MenuItem>
                <MenuItem value={4} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Fair" 
                      color="warning" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Fair
                  </Box>
                </MenuItem>
                <MenuItem value={5} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Poor" 
                      color="error" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Poor
                  </Box>
                </MenuItem>
              </Select>
              {hasError("condition") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("condition")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("shipping_method")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                }
              }}
            >
              <InputLabel>Shipping Method</InputLabel>
              <Select
                value={newAd.shipping_method}
                onChange={(e) => handleShippingMethodChange(e.target.value)}
                onBlur={() => handleFieldBlur("shipping_method")}
                label="Shipping Method"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                <MenuItem value={1} sx={{ fontFamily: professionalTheme.fontFamily }}>Pickup Only</MenuItem>
                <MenuItem value={2} sx={{ fontFamily: professionalTheme.fontFamily }}>Delivery Only</MenuItem>
              </Select>
              {hasError("shipping_method") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("shipping_method")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {shouldShowPickupLocation() && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pickup Location"
                placeholder="Enter pickup location address"
                value={newAd.pickup_location}
                onChange={(e) => handleInputChange("pickup_location", e.target.value)}
                onBlur={() => handleFieldBlur("pickup_location")}
                error={hasError("pickup_location")}
                helperText={getErrorMessage("pickup_location") || `${newAd.pickup_location.length}/255 characters`}
                disabled={isApiLoader}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    fontFamily: professionalTheme.fontFamily,
                    backgroundColor: professionalTheme.gray[50],
                    '&:hover fieldset': {
                      borderColor: professionalTheme.black,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: professionalTheme.black,
                      borderWidth: 2,
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: professionalTheme.fontFamily,
                    '&.Mui-focused': {
                      color: professionalTheme.black,
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontFamily: professionalTheme.fontFamily,
                  }
                }}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: professionalTheme.gray[600] }} />
                }}
              />
            </Grid>
          )}

          {/* Payment Methods Section */}
          <Grid item xs={12}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Payment Methods
              {isPaymentCompleted && (
                <Chip 
                  label="PAID" 
                  color="success" 
                  size="small" 
                  sx={{ ml: 2, fontFamily: professionalTheme.fontFamily }}
                />
              )}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily,
                mb: 3
              }}
            >
              Choose how you want to receive payment for this item.
            </Typography>
          </Grid>

          {/* Payment Method Type Selection */}
          <Grid item xs={12}>
            <FormControl 
              fullWidth
              error={hasError("payment_method_type")}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.black,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.black,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.black,
                  }
                }
              }}
            >
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={newAd.payment_method_type}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange("payment_method_type", value);
                  // Clear online payment fields when switching to cash
                  if (value === "cash") {
                    setNewAd(prev => ({
                      ...prev,
                      venmo_username: "",
                      paypal_username: ""
                    }));
                    setErrors(prev => ({
                      ...prev,
                      payment_methods: ""
                    }));
                  }
                }}
                onBlur={() => handleFieldBlur("payment_method_type")}
                label="Payment Method"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                <MenuItem value="online" sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Payment sx={{ mr: 2, color: professionalTheme.gray[600] }} />
                    Online Payment (Venmo/PayPal)
                  </Box>
                </MenuItem>
                <MenuItem value="cash" sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <AttachMoney sx={{ mr: 2, color: professionalTheme.gray[600] }} />
                    Cash Only
                  </Box>
                </MenuItem>
              </Select>
              {hasError("payment_method_type") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("payment_method_type")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Cash Only Information */}
          {newAd.payment_method_type === "cash" && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffb74d'
                }}
              >
                <Box display="flex" alignItems="center">
                  <AttachMoney sx={{ color: '#f57c00', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#ef6c00',
                        fontFamily: professionalTheme.fontFamily,
                        mb: 0.5
                      }}
                    >
                      Cash Only Payment
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#f57c00',
                        fontFamily: professionalTheme.fontFamily
                      }}
                    >
                      You have selected cash only payment. Buyers will know to bring cash for pickup or arrange cash payment for delivery.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Online Payment Fields - Only show when online payment is selected */}
          {newAd.payment_method_type === "online" && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Venmo Username"
                  placeholder="Enter your Venmo username (e.g., john-doe)"
                  value={newAd.venmo_username}
                  onChange={(e) => handleInputChange("venmo_username", e.target.value)}
                  onBlur={() => handleFieldBlur("venmo_username")}
                  error={hasError("payment_methods") && !newAd.venmo_username && !newAd.paypal_username}
                  helperText={
                    isPaymentCompleted && newAd.venmo_username ? 
                      "✅ Venmo payment completed" : 
                      "Optional - Just your username, we'll create the payment link"
                  }
                  disabled={isApiLoader}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: isPaymentCompleted && newAd.venmo_username ? '#e8f5e8' : professionalTheme.gray[50],
                      '&:hover fieldset': {
                        borderColor: professionalTheme.black,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: professionalTheme.black,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: professionalTheme.fontFamily,
                      '&.Mui-focused': {
                        color: professionalTheme.black,
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: professionalTheme.fontFamily,
                      color: isPaymentCompleted && newAd.venmo_username ? '#4caf50' : 'inherit'
                    }
                  }}
                  InputProps={{
                    startAdornment: <Payment sx={{ mr: 1, color: professionalTheme.gray[600] }} />,
                    endAdornment: isPaymentCompleted && newAd.venmo_username && (
                      <CheckCircle sx={{ color: '#4caf50', ml: 1 }} />
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PayPal Username"
                  placeholder="Enter your PayPal username (e.g., john-doe)"
                  value={newAd.paypal_username}
                  onChange={(e) => handleInputChange("paypal_username", e.target.value)}
                  onBlur={() => handleFieldBlur("paypal_username")}
                  error={hasError("payment_methods") && !newAd.venmo_username && !newAd.paypal_username}
                  helperText={
                    isPaymentCompleted && newAd.paypal_username ? 
                      "✅ PayPal payment completed" : 
                      "Optional - Just your username, we'll create the PayPal.me link"
                  }
                  disabled={isApiLoader}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: isPaymentCompleted && newAd.paypal_username ? '#e8f5e8' : professionalTheme.gray[50],
                      '&:hover fieldset': {
                        borderColor: professionalTheme.black,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: professionalTheme.black,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: professionalTheme.fontFamily,
                      '&.Mui-focused': {
                        color: professionalTheme.black,
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: professionalTheme.fontFamily,
                      color: isPaymentCompleted && newAd.paypal_username ? '#4caf50' : 'inherit'
                    }
                  }}
                  InputProps={{
                    startAdornment: <AccountBalance sx={{ mr: 1, color: professionalTheme.gray[600] }} />,
                    endAdornment: isPaymentCompleted && newAd.paypal_username && (
                      <CheckCircle sx={{ color: '#4caf50', ml: 1 }} />
                    )
                  }}
                />
              </Grid>

              {/* Show error message if no payment method is provided for online payments */}
              {hasError("payment_methods") && (
                <Grid item xs={12}>
                  <Typography 
                    color="error" 
                    variant="body2" 
                    sx={{ 
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: '#ffebee',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #f44336'
                    }}
                  >
                    {getErrorMessage("payment_methods")}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderImagesAndMedia = () => (
    <Card 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        backgroundColor: professionalTheme.white,
        border: `1px solid ${professionalTheme.gray[300]}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: professionalTheme.black,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
            }}
          >
            <ImageIcon sx={{ color: professionalTheme.white, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Images & Media
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily
              }}
            >
              Upload high-quality images to showcase your item
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Thumbnail Upload */}
          <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              gutterBottom 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Thumbnail (Main Image) *
            </Typography>
            <Paper
              elevation={0}
              sx={{
                border: `2px dashed ${hasError("thumbnail") ? "#f44336" : professionalTheme.gray[400]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: professionalTheme.gray[50],
                '&:hover': {
                  borderColor: professionalTheme.black,
                  backgroundColor: professionalTheme.gray[100],
                }
              }}
              onClick={() => !isApiLoader && document.getElementById("thumbnail-upload").click()}
            >
              {thumbnailPreview ? (
                <Box position="relative" display="inline-block">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    style={{ 
                      maxWidth: 200, 
                      maxHeight: 200, 
                      borderRadius: 8, 
                      objectFit: 'cover',
                      border: `1px solid ${professionalTheme.gray[300]}`
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: professionalTheme.black,
                      color: professionalTheme.white,
                      '&:hover': { 
                        backgroundColor: professionalTheme.gray[800],
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewAd((prev) => ({ ...prev, thumbnail: null }));
                      setThumbnailPreview(null);
                    }}
                    disabled={isApiLoader}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, color: professionalTheme.gray[600], mb: 2 }} />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontFamily: professionalTheme.fontFamily,
                      color: professionalTheme.black
                    }}
                  >
                    Upload Thumbnail
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: professionalTheme.gray[600],
                      fontFamily: professionalTheme.fontFamily,
                      mb: 2
                    }}
                  >
                    Click to select your main image
                  </Typography>
                  <Chip 
                    label="Max 2MB" 
                    size="small"
                    sx={{ 
                      backgroundColor: professionalTheme.gray[200],
                      color: professionalTheme.gray[700],
                      fontFamily: professionalTheme.fontFamily,
                      fontWeight: 500
                    }}
                  />
                </Box>
              )}
            </Paper>
            
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="thumbnail-upload"
              type="file"
              disabled={isApiLoader}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({ ...prev, thumbnail: "File size must be less than 2MB" }));
                    return;
                  }
                  setNewAd((prev) => ({ ...prev, thumbnail: file }));
                  setThumbnailPreview(URL.createObjectURL(file));
                  setErrors((prev) => ({ ...prev, thumbnail: "" }));
                  setTouched((prev) => ({ ...prev, thumbnail: true }));
                }
              }}
            />

            {hasError("thumbnail") && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ 
                  mt: 1, 
                  fontFamily: professionalTheme.fontFamily
                }}
              >
                {getErrorMessage("thumbnail")}
              </Typography>
            )}
          </Grid>

          {/* Additional Images - Now Optional */}
          <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              gutterBottom 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Additional Images (Optional - Up to 5 Images)
            </Typography>
            
            {/* Upload Area */}
            <Paper
              elevation={0}
              sx={{
                border: `2px dashed ${professionalTheme.gray[400]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: isImageLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: professionalTheme.gray[50],
                opacity: isImageLoading ? 0.7 : 1,
                position: 'relative',
                '&:hover': !isImageLoading ? {
                  borderColor: professionalTheme.black,
                  backgroundColor: professionalTheme.gray[100],
                } : {}
              }}
              onClick={() => !isApiLoader && !isImageLoading && selectedImageFiles.length < 5 && document.getElementById("images-upload").click()}
            >
              {/* Loading Overlay */}
              {isImageLoading && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="rgba(255, 255, 255, 0.9)"
                  borderRadius={2}
                  zIndex={1}
                >
                  <img
                    src={loadingGif}
                    alt="Loading..."
                    style={{ width: 32, height: 32, marginBottom: 16 }}
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 600,
                      color: professionalTheme.black,
                      fontFamily: professionalTheme.fontFamily
                    }}
                  >
                    Processing Images...
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: professionalTheme.gray[600],
                      fontFamily: professionalTheme.fontFamily,
                      mt: 0.5
                    }}
                  >
                    Please wait while we validate and prepare your images
                  </Typography>
                </Box>
              )}

              {/* Upload Content */}
              {selectedImageFiles.length < 5 ? (
                <>
                  <CloudUpload sx={{ fontSize: 48, color: professionalTheme.gray[600], mb: 2 }} />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontFamily: professionalTheme.fontFamily,
                      color: professionalTheme.black
                    }}
                  >
                    {selectedImageFiles.length === 0 ? 
                      "Upload Additional Images (Optional)" : 
                      `Add More Images (${selectedImageFiles.length}/5)`
                    }
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: professionalTheme.gray[600],
                      fontFamily: professionalTheme.fontFamily,
                      mb: 2
                    }}
                  >
                    {selectedImageFiles.length === 0 ? 
                      "Click to select additional images to showcase your item" :
                      `Click to add up to ${5 - selectedImageFiles.length} more image${5 - selectedImageFiles.length !== 1 ? 's' : ''}`
                    }
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontFamily: professionalTheme.fontFamily,
                      color: '#4caf50'
                    }}
                  >
                    Maximum Images Reached
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: professionalTheme.gray[600],
                      fontFamily: professionalTheme.fontFamily,
                      mb: 2
                    }}
                  >
                    You have uploaded the maximum of 5 images. Remove an image to add a different one.
                  </Typography>
                </>
              )}

              <Box display="flex" justifyContent="center" gap={1}>
                <Chip 
                  label="Optional" 
                  size="small"
                  sx={{ 
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontFamily: professionalTheme.fontFamily,
                    fontWeight: 500
                  }}
                />
                <Chip 
                  label="Up to 5 images" 
                  size="small"
                  sx={{ 
                    backgroundColor: professionalTheme.primary,
                    color: professionalTheme.white,
                    fontFamily: professionalTheme.fontFamily,
                    fontWeight: 500
                  }}
                />
                <Chip 
                  label="Max 5MB each" 
                  size="small"
                  sx={{ 
                    backgroundColor: professionalTheme.gray[200],
                    color: professionalTheme.gray[700],
                    fontFamily: professionalTheme.fontFamily,
                    fontWeight: 500
                  }}
                />
              </Box>
            </Paper>

            <input
              accept="image/*"
              style={{ display: "none" }}
              id="images-upload"
              type="file"
              multiple
              disabled={isApiLoader || isImageLoading || selectedImageFiles.length >= 5}
              onChange={async (e) => {
                const newFiles = Array.from(e.target.files);
                
                if (newFiles.length === 0) return;

                // Start loading
                setIsImageLoading(true);

                try {
                  // Small delay to show loader
                  await new Promise(resolve => setTimeout(resolve, 500));

                  // Get current files and calculate total
                  const existingFiles = [...selectedImageFiles];
                  const existingUrls = [...imagePreviewUrls];
                  const totalFiles = existingFiles.length + newFiles.length;

                  // Check if total would exceed 5 images
                  if (totalFiles > 5) {
                    const remainingSlots = 5 - existingFiles.length;
                    if (remainingSlots > 0) {
                      toast.error(`You can only add ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''}. Maximum limit is 5 images.`);
                    } else {
                      toast.error(`You already have the maximum of 5 images.`);
                    }
                    e.target.value = '';
                    return;
                  }

                  // Check file sizes
                  const oversizedFiles = newFiles.filter(file => file.size > 5 * 1024 * 1024);
                  if (oversizedFiles.length > 0) {
                    toast.error(`Some files are too large. Each file must be less than 5MB.`);
                    e.target.value = '';
                    return;
                  }

                  // Process new files and create previews
                  const newImageUrls = await Promise.all(
                    newFiles.map((file, index) => {
                      return new Promise((resolve) => {
                        const url = URL.createObjectURL(file);
                        setTimeout(() => resolve(url), index * 100 + 200);
                      });
                    })
                  );

                  // Combine existing and new files/urls
                  const combinedFiles = [...existingFiles, ...newFiles];
                  const combinedUrls = [...existingUrls, ...newImageUrls];

                  setSelectedImageFiles(combinedFiles);
                  setImagePreviewUrls(combinedUrls);

                  // Clear the input for next selection
                  e.target.value = '';

                } catch (error) {
                  console.error('Error processing images:', error);
                } finally {
                  setIsImageLoading(false);
                }
              }}
            />

            {/* Image Previews with Loading States */}
            {(imagePreviewUrls.length > 0 || isImageLoading) && (
              <Box mt={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      fontFamily: professionalTheme.fontFamily,
                      color: professionalTheme.black
                    }}
                  >
                    {isImageLoading ? 
                      "Processing Images..." : 
                      `Selected Images (${imagePreviewUrls.length}/5)`
                    }
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {imagePreviewUrls.length >= 1 && imagePreviewUrls.length <= 5 && !isImageLoading && (
                      <Chip
                        icon={<CheckCircle sx={{ fontSize: 16 }} />}
                        label={imagePreviewUrls.length === 5 ? "Maximum Reached" : "Valid"}
                        size="small"
                        sx={{
                          backgroundColor: imagePreviewUrls.length === 5 ? '#e3f2fd' : '#e8f5e8',
                          color: imagePreviewUrls.length === 5 ? '#1976d2' : '#4caf50',
                          fontFamily: professionalTheme.fontFamily,
                          fontWeight: 500
                        }}
                      />
                    )}
                    {imagePreviewUrls.length === 0 && !isImageLoading && (
                      <Chip
                        label="Optional"
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontFamily: professionalTheme.fontFamily,
                          fontWeight: 500
                        }}
                      />
                    )}
                    {imagePreviewUrls.length < 5 && imagePreviewUrls.length > 0 && !isImageLoading && (
                      <Chip
                        label={`${5 - imagePreviewUrls.length} more allowed`}
                        size="small"
                        sx={{
                          backgroundColor: '#fff3e0',
                          color: '#f57c00',
                          fontFamily: professionalTheme.fontFamily,
                          fontWeight: 500
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Actual Image Grid */}
                {!isImageLoading && imagePreviewUrls.length > 0 && (
                  <Grid container spacing={2}>
                    {imagePreviewUrls.map((url, index) => (
                      <Grid item xs={6} sm={4} md={2.4} key={`image-${index}`}>
                        <Paper 
                          elevation={1} 
                          sx={{ 
                            position: 'relative', 
                            p: 1,
                            borderRadius: 2,
                            border: `1px solid ${professionalTheme.gray[300]}`,
                            animation: 'fadeIn 0.3s ease-in-out'
                          }}
                        >
                          <img
                            src={url}
                            alt={`Preview ${index + 1}`}
                            style={{ 
                              width: '100%', 
                              height: 120, 
                              objectFit: 'cover', 
                              borderRadius: 4
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: 8,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: 1,
                              fontSize: '10px',
                              fontFamily: professionalTheme.fontFamily
                            }}
                          >
                            {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              backgroundColor: professionalTheme.black,
                              color: professionalTheme.white,
                              '&:hover': { 
                                backgroundColor: professionalTheme.gray[800],
                              }
                            }}
                            onClick={() => {
                              const newFiles = selectedImageFiles.filter((_, i) => i !== index);
                              const newUrls = imagePreviewUrls.filter((_, i) => i !== index);
                              setSelectedImageFiles(newFiles);
                              setImagePreviewUrls(newUrls);
                            }}
                            disabled={isApiLoader || isImageLoading}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Paper>
                      </Grid>
                    ))}

                    {/* Show placeholder slots for remaining images (optional) */}
                    {!isImageLoading && imagePreviewUrls.length < 5 && imagePreviewUrls.length > 0 && (
                      <>
                        {[...Array(Math.min(2, 5 - imagePreviewUrls.length))].map((_, index) => (
                          <Grid item xs={6} sm={4} md={2.4} key={`placeholder-${index}`}>
                            <Paper 
                              elevation={0} 
                              sx={{ 
                                p: 1,
                                borderRadius: 2,
                                border: `2px dashed ${professionalTheme.gray[300]}`,
                                backgroundColor: professionalTheme.gray[100],
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: professionalTheme.black,
                                  backgroundColor: professionalTheme.gray[200],
                                }
                              }}
                              onClick={() => document.getElementById("images-upload").click()}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 120,
                                  borderRadius: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: professionalTheme.gray[500]
                                }}
                              >
                                <CloudUpload sx={{ fontSize: 32, mb: 1 }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '10px',
                                    fontFamily: professionalTheme.fontFamily,
                                    textAlign: 'center'
                                  }}
                                >
                                  Optional
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  left: 8,
                                  backgroundColor: 'rgba(158,158,158,0.7)',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: 1,
                                  fontSize: '10px',
                                  fontFamily: professionalTheme.fontFamily
                                }}
                              >
                                {imagePreviewUrls.length + index + 1}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </>
                    )}
                  </Grid>
                )}

                {/* Loading Grid */}
                {isImageLoading && (
                  <Grid container spacing={2}>
                    {[...Array(Math.min(5, imagePreviewUrls.length + 3))].map((_, index) => (
                      <Grid item xs={6} sm={4} md={2.4} key={`loading-${index}`}>
                        <Paper 
                          elevation={1} 
                          sx={{ 
                            position: 'relative', 
                            p: 1,
                            borderRadius: 2,
                            border: `1px solid ${professionalTheme.gray[300]}`,
                            backgroundColor: professionalTheme.gray[100]
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              height: 120,
                              borderRadius: 1,
                              backgroundColor: professionalTheme.gray[200],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              animation: 'pulse 1.5s ease-in-out infinite'
                            }}
                          >
                            <img
                              src={loadingGif}
                              alt="Loading..."
                              style={{ width: 24, height: 24 }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'absolute',
                              bottom: 4,
                              left: 8,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: 1,
                              fontSize: '10px',
                              fontFamily: professionalTheme.fontFamily
                            }}
                          >
                            {index + 1}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
                
                {/* Progress indicator */}
                {!isImageLoading && imagePreviewUrls.length === 0 && (
                  <Box mt={2}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: professionalTheme.gray[600],
                        fontFamily: professionalTheme.fontFamily,
                        textAlign: 'center',
                        fontWeight: 500
                      }}
                    >
                      Additional images are optional - add up to 5 images to showcase your item better
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderBasicInformation();
      case 1:
        return renderCategoryAndDetails();
      case 2:
        return renderImagesAndMedia();
      default:
        return null;
    }
  };

// Updated form validation - removed additional images requirement
const isFormValid = () => {
  const hasRequiredFields = newAd.name && newAd.description && newAd.price && 
                           newAd.category_id && newAd.sub_category_id && 
                           newAd.condition && newAd.shipping_method &&
                           newAd.payment_method_type;
  
  const hasPaymentMethod = (
    newAd.payment_method_type === "cash" ||
    (newAd.venmo_username && newAd.venmo_username.trim() !== "") ||
    (newAd.paypal_username && newAd.paypal_username.trim() !== "")
  );
  
  const hasPickupLocationIfNeeded = !shouldShowPickupLocation() || newAd.pickup_location;
  
  // Only thumbnail is required now - additional images are completely optional
  const hasValidImages = newAd.thumbnail;
  
  const hasNoErrors = Object.keys(errors).every(key => !errors[key] || errors[key] === "");
  
  return hasRequiredFields && hasPaymentMethod && hasPickupLocationIfNeeded && hasValidImages && hasNoErrors;
};

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        py: 4,
        fontFamily: professionalTheme.fontFamily
      }}
    >
      <LoadingOverlay 
        isLoading={isApiLoader} 
        isPaymentCompleted={isPaymentCompleted}
        isDraftSaving={isDraftSaving}
      />
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
      
      <Box maxWidth="lg" mx="auto" px={3}>
        {/* Header */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 2,
            backgroundColor: professionalTheme.white,
            border: `1px solid ${professionalTheme.gray[300]}`
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <IconButton 
                onClick={() => navigate("/myaccount/mylisting")}
                sx={{ 
                  mr: 3,
                  backgroundColor: professionalTheme.gray[100],
                  '&:hover': {
                    backgroundColor: professionalTheme.gray[200],
                  }
                }}
                disabled={isApiLoader}
              >
                <ArrowBack sx={{ color: professionalTheme.black }} />
              </IconButton>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1,
                    fontFamily: professionalTheme.fontFamily,
                    color: professionalTheme.black
                  }}
                >
                  {isUpdate ? "Edit Listing" : "Create New Listing"}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: professionalTheme.gray[600],
                    fontFamily: professionalTheme.fontFamily
                  }}
                >
                  {isUpdate ? "Update your existing listing with new details" : "Create a professional listing to reach more buyers"}
                </Typography>
              </Box>
            </Box>
            
            {/* Payment Status Indicator */}
            {isUpdate && isPaymentCompleted && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: 18 }} />}
                label="Payment Completed"
                sx={{
                  backgroundColor: '#e8f5e8',
                  color: '#4caf50',
                  fontFamily: professionalTheme.fontFamily,
                  fontWeight: 600,
                  fontSize: '14px',
                  '& .MuiChip-icon': {
                    color: '#4caf50'
                  }
                }}
              />
            )}
          </Box>
        </Paper>

        {/* Payment Status Alert */}
        {isUpdate && isPaymentCompleted && (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              backgroundColor: '#e8f5e8',
              border: '1px solid #4caf50'
            }}
          >
            <Box display="flex" alignItems="center">
              <CheckCircle sx={{ color: '#4caf50', mr: 2, fontSize: 28 }} />
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#2e7d32',
                    fontFamily: professionalTheme.fontFamily,
                    mb: 0.5
                  }}
                >
                  Payment Completed ✅
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#388e3c',
                    fontFamily: professionalTheme.fontFamily
                  }}
                >
                  The buyer has successfully completed the payment for this item. You can now proceed with delivery/pickup arrangements.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Stepper */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 2,
            backgroundColor: professionalTheme.white,
            border: `1px solid ${professionalTheme.gray[300]}`
          }}
        >
          <ProfessionalStepper activeStep={activeStep} steps={steps} />
        </Paper>

        {/* Form Content */}
        <Box mb={4}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            backgroundColor: professionalTheme.white,
            border: `1px solid ${professionalTheme.gray[300]}`
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || isApiLoader}
              startIcon={<ArrowBack />}
              size="large"
              sx={{
                borderRadius: 1,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '16px',
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.gray[700],
                border: `1px solid ${professionalTheme.gray[400]}`,
                '&:hover': {
                  backgroundColor: professionalTheme.gray[100],
                  borderColor: professionalTheme.black,
                }
              }}
            >
              Back 
            </Button>

            <Box display="flex" gap={3}>              
              {/* Save as Draft Button - Always available, no validation */}
              <Button
                variant="outlined"
                onClick={handleDraftClick}
                disabled={isApiLoader || isDraftSaving}
                startIcon={<Save />}
                size="large"
                title="Save your progress as a draft - you can continue editing later"
                sx={{
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '16px',
                  fontFamily: professionalTheme.fontFamily,
                  color: professionalTheme.gray[700],
                  borderColor: professionalTheme.gray[400],
                  height:'55px',
                  marginTop: "14px",
                  width:'180px',
                  '&:hover': {
                    backgroundColor: professionalTheme.gray[100],
                    borderColor: professionalTheme.black,
                    color: professionalTheme.black,
                  },
                  '&:disabled': {
                    color: professionalTheme.gray[400],
                    borderColor: professionalTheme.gray[300],
                    height:'55px',
                    marginTop: "14px",
                    width:'180px',
                  }
                }}
              >
                {isDraftSaving ? "Saving..." : "Save Draft"}
              </Button>

              {/* Payment Completed Button */}
              {isUpdate && adId && (
                <Button
                  variant="outlined"
                  onClick={() => markPaymentCompleted(adId)}
                  disabled={isApiLoader || isPaymentCompleted}
                  startIcon={<CheckCircle />}
                  size="large"
                  sx={{
                    borderRadius: 1,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '16px',
                    fontFamily: professionalTheme.fontFamily,
                    color: isPaymentCompleted ? '#9e9e9e' : '#4caf50',
                    borderColor: isPaymentCompleted ? '#9e9e9e' : '#4caf50',
                    '&:hover': {
                      backgroundColor: isPaymentCompleted ? 'transparent' : '#e8f5e8',
                      borderColor: isPaymentCompleted ? '#9e9e9e' : '#4caf50',
                    },
                    '&:disabled': {
                      color: '#9e9e9e',
                      borderColor: '#9e9e9e',
                    }
                  }}
                >
                  {isPaymentCompleted ? "Payment Already Completed" : "Mark Payment Completed"}
                </Button>
              )}

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={submitAd}
                  disabled={isApiLoader}
                  startIcon={<Publish />}
                  size="large"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '16px',
                    fontFamily: professionalTheme.fontFamily,
                    backgroundColor: professionalTheme.primary,
                      height:'55px',
                    marginTop: "14px",
                    width: '180px',
                    '&:hover': {
                      backgroundColor: '#b8489e',
                    },
                    '&:disabled': {
                      backgroundColor: professionalTheme.gray[400],
                      color: professionalTheme.gray[600]
                    }
                  }}
                >
                  {isUpdate ? "Update Ad" : "Publish Ad"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid() || isApiLoader}
                  endIcon={<ArrowForward />}
                  size="large"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '16px',
                    padding:'0 !importent',
                    fontFamily: professionalTheme.fontFamily,
                    backgroundColor: professionalTheme.primary,
                    height:'55px',
                    marginTop: "14px",
                     width:'180px',
                    '&:hover': {
                      backgroundColor: '#b8489e',
                    },
                    '&:disabled': {
                      backgroundColor: professionalTheme.gray[400],
                      color: professionalTheme.gray[600]
                    }
                  }}
                >
                  Next Step
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateAdPage;